import { logError } from "./logError";
import { buildN8nWebhookUrl } from "./n8nUrl";
export interface ChatMessage {
  id: string;
  role: "user" | "bot";
  content: string;
  timestamp: number;
}

export interface ChatPayload {
  message: string;
  sessionId: string;
  email?: string;
  firstName?: string;
  userId?: string;
  canViewFinancials?: boolean;
  canViewVault?: boolean;
  userType?: "standard" | "financial" | "vault" | "executive";
}

const WEBHOOK_URL = buildN8nWebhookUrl(
  import.meta.env.VITE_N8N_BASE_URL,
  "claims-ai-bot",
);
const SESSION_KEY = "claims_chat_session_id";

/**
 * Retrieves or creates a persistent session ID for the chat.
 */
export function getSessionId(): string {
  let sessionId = localStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

/**
 * Sends a message to the Claims Bot n8n webhook.
 */
export async function sendChatMessage(
  message: string,
  email?: string,
  firstName?: string,
  userId?: string,
  canViewFinancials?: boolean,
  canViewVault?: boolean,
): Promise<string> {
  const sessionId = getSessionId();

  try {
    const res = await fetch(WEBHOOK_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        message,
        sessionId,
        email,
        firstName,
        userId,
        canViewFinancials,
        canViewVault,
        userType: canViewFinancials && canViewVault
          ? "executive"
          : canViewFinancials
            ? "financial"
            : canViewVault
              ? "vault"
              : "standard",
      } as ChatPayload),
    });

    if (!res.ok) {
      throw new Error(`Chat API error: ${res.statusText}`);
    }

    // n8n webhooks can return various formats, so handle them safely
    const data = await res.json();

    if (data && typeof data.output === "string") return data.output;
    if (data && typeof data.text === "string") return data.text;
    if (data && typeof data.response === "string") return data.response;
    if (Array.isArray(data) && data.length > 0 && data[0].text)
      return data[0].text;
    if (Array.isArray(data) && data.length > 0 && data[0].output)
      return data[0].output;

    return typeof data === "string" ? data : JSON.stringify(data);
  } catch (error) {
    logError("Failed to send chat message:", error);
    throw error;
  }
}
